/**
 * Runtime dos overlays/modais browser-driven do kiosk.
 *
 * As funções recebem o hook como primeiro argumento para preservar o
 * contrato atual de `Hooks.ValidationErrors` sem alterar a API do LiveView.
 */

export const kioskModalOverlayIds = () => [
  'walk-in-confirm-modal',
  'walk-in-conflict-modal',
  'walk-in-success-modal',
  'checkin-confirm-modal',
  'checkin-success-modal',
  'already-checked-in-modal',
  'already-served-modal',
  'cancellation-success-modal',
  'kiosk-confirm-action-modal',
  'kiosk-option-action-sheet',
  'kiosk-modern-alert',
  'kiosk-emoji-alert',
  'operation-alert',
  'validation-alert',
  'validation-option-sheet'
];

const isVisibleModalElement = (element) => !!(element && !element.classList.contains('hidden'));

const clearNodeChildren = (element) => {
  if (!element) return;

  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
};

const populateDetailCards = (
  container,
  items,
  {
    cardClass,
    labelClass,
    valueClass,
    labelTag = 'div',
    valueTag = 'div'
  }
) => {
  if (!container) return;

  clearNodeChildren(container);

  items.forEach(({ label, value }) => {
    const card = document.createElement('div');
    const labelEl = document.createElement(labelTag);
    const valueEl = document.createElement(valueTag);

    card.className = cardClass;
    labelEl.className = labelClass;
    valueEl.className = valueClass;
    labelEl.textContent = label;
    valueEl.textContent = value;

    card.appendChild(labelEl);
    card.appendChild(valueEl);
    container.appendChild(card);
  });
};

const setRichMessage = (element, primaryText, secondaryText, secondaryClass = 'text-slate-500') => {
  if (!element) return;

  clearNodeChildren(element);

  const primaryNode = document.createTextNode(primaryText);
  const lineBreak = document.createElement('br');
  const secondaryNode = document.createElement('span');

  secondaryNode.className = secondaryClass;
  secondaryNode.textContent = secondaryText;

  element.appendChild(primaryNode);
  element.appendChild(lineBreak);
  element.appendChild(secondaryNode);
};

const fallbackStatusGlyph = (type) => {
  const glyphs = {
    success: 'OK',
    error: '!',
    warning: '!',
    info: 'i',
    blocked: '!',
    disabled: '!',
    'user-x': 'ID',
    'shield-x': '!',
    'alert-triangle': '!',
    'credit-card': 'ID'
  };

  return glyphs[type] || '!';
};

const setOptionalTextIcon = (element, value) => {
  if (!element) return;

  const textValue = value == null ? '' : String(value).trim();

  if (textValue) {
    element.textContent = textValue;
    element.classList.remove('hidden');
    return;
  }

  element.textContent = '';
  element.classList.add('hidden');
};

const closeModalElement = (element) => {
  if (!element) return;

  if (element.dataset.staticModalHost === 'true') {
    element.classList.add('hidden');
    return;
  }

  element.remove();
};

export const removeKioskModals = (ids = []) => {
  ids.forEach((id) => {
    const element = document.getElementById(id);
    closeModalElement(element);
  });
};

export const closeKnownKioskModals = (hook, ids = kioskModalOverlayIds(), options = {}) => {
  removeKioskModals(ids);

  if (options.sync !== false) {
    syncKioskInterfaceControls(hook);
  }
};

export const closeCheckinConfirmationModal = (hook, options = {}) =>
  closeKnownKioskModals(hook, ['checkin-confirm-modal'], options);

export const closeKioskSuccessModals = (hook, options = {}) =>
  closeKnownKioskModals(hook, ['checkin-success-modal', 'walk-in-success-modal'], options);

export const closeCheckinFlowBrowserModals = (hook, options = {}) => {
  closeKnownKioskModals(hook, kioskModalOverlayIds(), options);
  if (hook) {
    hook._activeAuthAttempt = null;
    hook._lastEmojiAlertSignature = null;
  }
};

export const hideKioskInterfaceControls = () => {
  document.body.classList.add('kiosk-browser-modal-open');
  document.querySelectorAll('.kiosk-interface-controls').forEach((element) => {
    element.style.display = '';
    delete element.dataset.prevDisplay;
  });
};

export const showKioskInterfaceControls = () => {
  document.body.classList.remove('kiosk-browser-modal-open');
  document.querySelectorAll('.kiosk-interface-controls').forEach((element) => {
    element.style.display = '';
    delete element.dataset.prevDisplay;
  });
};

export const syncKioskInterfaceControls = (_hook) => {
  const hasBrowserModal = kioskModalOverlayIds().some((id) =>
    isVisibleModalElement(document.getElementById(id))
  );

  document.body.classList.toggle('kiosk-browser-modal-open', hasBrowserModal);

  document.querySelectorAll('.kiosk-interface-controls').forEach((element) => {
    element.style.display = '';
    delete element.dataset.prevDisplay;
  });
};

export const showModernAlert = (_hook, title, text, icon) => {
  const staticOverlay = document.getElementById('kiosk-modern-alert');
  if (staticOverlay) {
    const iconWrap = staticOverlay.querySelector('[data-role="icon-wrap"]');
    const iconSvg = staticOverlay.querySelector('[data-role="icon-svg"]');
    const iconPath = staticOverlay.querySelector('[data-role="icon-path"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const textEl = staticOverlay.querySelector('[data-role="text"]');
    const okBtn = staticOverlay.querySelector('[data-role="ok"]');
    const close = () => staticOverlay.classList.add('hidden');

    if (iconWrap && iconSvg && iconPath && titleEl && textEl && okBtn) {
      let bgClass = 'bg-blue-100';
      let textClass = 'text-blue-600';
      let btnClass = 'bg-blue-600 hover:bg-blue-700';
      let pathD = 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';

      if (icon === 'error') {
        bgClass = 'bg-red-100';
        textClass = 'text-red-600';
        btnClass = 'bg-red-600 hover:bg-red-700';
        pathD = 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
      }

      if (icon === 'success') {
        bgClass = 'bg-emerald-100';
        textClass = 'text-emerald-600';
        btnClass = 'bg-emerald-600 hover:bg-emerald-700';
        pathD = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
      }

      iconWrap.className = `flex-shrink-0 w-10 h-10 rounded-full ${bgClass} flex items-center justify-center`;
      iconSvg.className = `w-6 h-6 ${textClass}`;
      iconPath.setAttribute('d', pathD);
      titleEl.textContent = title;
      textEl.textContent = text;
      okBtn.className = `px-6 py-2 ${btnClass} text-white rounded-lg font-medium transition-colors`;

      staticOverlay.classList.remove('hidden');
      okBtn.onclick = () => close();

      setTimeout(() => {
        if (!staticOverlay.classList.contains('hidden')) {
          close();
        }
      }, 6000);

      return;
    }
  }

  let bgClass = 'bg-blue-100';
  let textClass = 'text-blue-600';
  let btnClass = 'bg-blue-600 hover:bg-blue-700';

  if (icon === 'error') {
    bgClass = 'bg-red-100';
    textClass = 'text-red-600';
    btnClass = 'bg-red-600 hover:bg-red-700';
  }

  if (icon === 'success') {
    bgClass = 'bg-emerald-100';
    textClass = 'text-emerald-600';
    btnClass = 'bg-emerald-600 hover:bg-emerald-700';
  }

  const alertHtml = `
    <div id="validation-alert" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[9999]" style="z-index: 99999;">
      <div class="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 transform transition-all">
        <div class="flex items-center mb-4">
          <div class="flex-shrink-0">
            <div class="w-10 h-10 rounded-full ${bgClass} flex items-center justify-center">
              <svg class="w-6 h-6 ${textClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                ${icon === 'error'
                  ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>'
                  : (icon === 'success'
                    ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>'
                    : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>')
                }
              </svg>
            </div>
          </div>
          <div class="ml-4">
            <h3 class="text-lg font-semibold text-gray-900">${title}</h3>
          </div>
        </div>
        <div class="mb-6">
          <p class="text-sm text-gray-600">${text}</p>
        </div>
        <div class="flex justify-end">
          <button onclick="document.getElementById('validation-alert').remove()"
                  class="px-6 py-2 ${btnClass} text-white rounded-lg font-medium transition-colors">
            OK
          </button>
        </div>
      </div>
    </div>`;

  const existing = document.getElementById('validation-alert');
  if (existing && existing.dataset.staticModalHost === 'true') {
    const iconWrap = existing.querySelector('[data-role="icon-wrap"]');
    const iconSvg = existing.querySelector('[data-role="icon-svg"]');
    const iconPath = existing.querySelector('[data-role="icon-path"]');
    const titleEl = existing.querySelector('[data-role="title"]');
    const textEl = existing.querySelector('[data-role="text"]');
    const okBtn = existing.querySelector('[data-role="ok"]');
    const scrim = existing.querySelector('[data-role="scrim"]');
    const close = () => existing.classList.add('hidden');

    if (iconWrap && iconSvg && iconPath && titleEl && textEl && okBtn && scrim) {
      const pathD = icon === 'error'
        ? 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
        : (icon === 'success'
          ? 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
          : 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z');

      iconWrap.className = `w-10 h-10 rounded-full ${bgClass} flex items-center justify-center`;
      iconSvg.className = `w-6 h-6 ${textClass}`;
      iconPath.setAttribute('d', pathD);
      titleEl.textContent = title;
      textEl.textContent = text;
      okBtn.className = `px-6 py-2 ${btnClass} text-white rounded-lg font-medium transition-colors`;

      existing.classList.remove('hidden');
      existing.onclick = (event) => {
        if (event.target === existing || event.target === scrim) close();
      };
      okBtn.onclick = () => close();

      setTimeout(() => {
        if (!existing.classList.contains('hidden')) close();
      }, 6000);

      return;
    }
  }
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', alertHtml);
  setTimeout(() => {
    const alert = document.getElementById('validation-alert');
    if (alert) alert.remove();
  }, 6000);
};

export const showEmojiAlert = (hook, data) => {
  const { type = 'error', title = 'Alerta', message = '', icon = 'warning' } = data || {};
  let attemptRef = data && (data.attempt_ref ?? data.attemptRef);
  const canonicalReason = data && data.reason;

  if (hook._activeAuthAttempt != null) {
    if (attemptRef == null) {
      attemptRef = hook._activeAuthAttempt;
    } else if (attemptRef !== hook._activeAuthAttempt) {
      return;
    }
  }

  const signature = `${attemptRef ?? 'none'}::${canonicalReason || (message || title)}`;

  if (signature && hook._lastEmojiAlertSignature === signature) {
    return;
  }
  hook._lastEmojiAlertSignature = signature;

  const staticOverlay = document.getElementById('kiosk-emoji-alert');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#emoji-alert-modal');
    const iconEl = staticOverlay.querySelector('[data-role="icon"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const textEl = staticOverlay.querySelector('[data-role="text"]');
    const okBtn = staticOverlay.querySelector('[data-role="ok"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (modal && iconEl && titleEl && textEl && okBtn && scrim) {
      const displayMessage = (!message || message === 'nil') ? title : message;
      const close = () => {
        staticOverlay.classList.add('hidden');
        hook._activeAuthAttempt = null;
      };

      iconEl.textContent = fallbackStatusGlyph(icon);
      titleEl.textContent = title;
      textEl.textContent = displayMessage;

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };
      okBtn.onclick = () => close();

      setTimeout(() => {
        if (!staticOverlay.classList.contains('hidden')) {
          close();
        }
      }, 3000);

      return;
    }
  }

  const overlayId = 'validation-alert';
  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const displayMessage = (!message || message === 'nil') ? title : message;

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div class="bg-white/95 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/30 max-w-md mx-4 transform transition-all duration-300 scale-95 opacity-0" id="emoji-alert-modal">
        <div class="text-center">
          <div class="text-4xl mb-4 animate-bounce font-black">${fallbackStatusGlyph(icon)}</div>
          <h3 class="text-2xl font-bold text-slate-900 mb-2">${title}</h3>
          <p class="text-lg text-slate-600 mb-6 leading-relaxed">${displayMessage}</p>
          <button class="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg">
            OK
          </button>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('emoji-alert-modal');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = () => {
    if (overlay) {
      overlay.remove();
    }
    hook._activeAuthAttempt = null;
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('button').addEventListener('click', close);
  setTimeout(close, 3000);
};

export const showOperationAlert = (_hook, data) => {
  const { type = 'error', title = 'Alerta', message = '', icon = 'warning' } = data || {};

  const staticOverlay = document.getElementById('operation-alert');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#operation-alert-modal');
    const iconEl = staticOverlay.querySelector('[data-role="icon"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const textEl = staticOverlay.querySelector('[data-role="text"]');
    const actionBtn = staticOverlay.querySelector('[data-role="action"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (modal && iconEl && titleEl && textEl && actionBtn && scrim) {
      const iconColors = {
        success: 'text-emerald-600',
        error: 'text-red-600',
        warning: 'text-amber-600',
        info: 'text-blue-600',
        blocked: 'text-red-600',
        disabled: 'text-orange-600'
      };

      const buttonColors = {
        success: 'from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700',
        error: 'from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
        warning: 'from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
        info: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
        blocked: 'from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
        disabled: 'from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
      };

      const displayMessage = (!message || message === 'nil') ? title : message;
      const close = () => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => staticOverlay.classList.add('hidden'), 200);
      };

      iconEl.textContent = fallbackStatusGlyph(type);
      iconEl.className = `text-5xl mb-6 animate-bounce font-black ${iconColors[type] || iconColors.error}`;
      titleEl.textContent = title;
      textEl.textContent = displayMessage;
      actionBtn.className =
        `w-full bg-gradient-to-r ${buttonColors[type] || buttonColors.error} text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105`;

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };
      actionBtn.onclick = () => close();

      if (type !== 'error' && type !== 'blocked' && type !== 'disabled') {
        setTimeout(() => {
          if (!staticOverlay.classList.contains('hidden')) {
            close();
          }
        }, 5000);
      }

      return;
    }
  }

  const overlayId = 'operation-alert';
  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const iconColors = {
    success: 'text-emerald-600',
    error: 'text-red-600',
    warning: 'text-amber-600',
    info: 'text-blue-600',
    blocked: 'text-red-600',
    disabled: 'text-orange-600'
  };

  const buttonColors = {
    success: 'from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700',
    error: 'from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
    warning: 'from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
    info: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
    blocked: 'from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
    disabled: 'from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
  };

  const displayMessage = (!message || message === 'nil') ? title : message;

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999]">
      <div class="bg-white/95 backdrop-blur-xl rounded-3xl p-10 shadow-2xl border-2 border-white/40 max-w-lg mx-4 transform transition-all duration-300 scale-95 opacity-0" id="operation-alert-modal">
        <div class="text-center">
          <div class="text-5xl mb-6 animate-bounce font-black ${iconColors[type] || iconColors.error}">${fallbackStatusGlyph(type)}</div>
          <h3 class="text-3xl font-bold text-slate-900 mb-4">${title}</h3>
          <p class="text-xl text-slate-700 mb-8 leading-relaxed">${displayMessage}</p>
          <button class="w-full bg-gradient-to-r ${buttonColors[type] || buttonColors.error} text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
            Entendido
          </button>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('operation-alert-modal');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = () => {
    if (overlay) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => overlay.remove(), 200);
    }
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('button').addEventListener('click', close);

  if (type !== 'error' && type !== 'blocked' && type !== 'disabled') {
    setTimeout(close, 5000);
  }
};

export const showOptionAction = (hook, data) => {
  const { title = 'Escolher ação', text = '', options = [], target_selector = null } = data || {};

  const staticOverlay = document.getElementById('kiosk-option-action-sheet');
  if (staticOverlay) {
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const textEl = staticOverlay.querySelector('[data-role="text"]');
    const optionsHost = staticOverlay.querySelector('[data-role="options"]');
    const cancelBtn = staticOverlay.querySelector('[data-role="cancel"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (titleEl && textEl && optionsHost && cancelBtn && scrim) {
      const close = () => {
        staticOverlay.classList.add('hidden');
        optionsHost.replaceChildren();
        syncKioskInterfaceControls(hook);
      };

      titleEl.textContent = title;
      textEl.textContent = text || '';
      textEl.classList.toggle('hidden', !text);

      optionsHost.replaceChildren();

      (options || []).forEach((opt, idx) => {
        const btn = document.createElement('button');
        const label = opt.label || `Opção ${idx + 1}`;

        btn.type = 'button';
        btn.className =
          'w-full px-5 py-3 rounded-xl text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors';
        btn.textContent = label;
        btn.onclick = () => {
          try {
            const evt = opt.event || '';
            const payload = opt.data || {};

            if (evt) {
              if (target_selector && hook.pushEventTo) {
                hook.pushEventTo(target_selector, evt, payload);
              } else if (hook.pushEvent) {
                hook.pushEvent(evt, payload);
              }
            }
          } catch (error) {
            console.error('[showOptionAction] Error sending event:', error);
          } finally {
            close();
          }
        };

        optionsHost.appendChild(btn);
      });

      staticOverlay.classList.remove('hidden');
      syncKioskInterfaceControls(hook);

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };

      cancelBtn.onclick = () => close();
      return;
    }
  }

  const overlayId = 'validation-option-sheet';
  const existing = document.getElementById(overlayId);
  if (existing && existing.dataset.staticModalHost === 'true') {
    const titleEl = existing.querySelector('[data-role="title"]');
    const textEl = existing.querySelector('[data-role="text"]');
    const optionsHost = existing.querySelector('[data-role="options"]');
    const cancelBtn = existing.querySelector('[data-role="cancel"]');
    const scrim = existing.querySelector('[data-role="scrim"]');

    if (titleEl && textEl && optionsHost && cancelBtn && scrim) {
      const close = () => existing.classList.add('hidden');

      titleEl.textContent = title;
      textEl.textContent = text || '';
      textEl.classList.toggle('hidden', !text);
      optionsHost.replaceChildren();

      (options || []).forEach((opt, idx) => {
        const btn = document.createElement('button');
        const label = opt.label || `Opção ${idx + 1}`;

        btn.type = 'button';
        btn.className =
          'w-full px-5 py-3 rounded-xl text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors';
        btn.textContent = label;
        btn.onclick = () => {
          try {
            const evt = opt.event || '';
            const payload = opt.data || {};

            if (evt) {
              if (target_selector && hook.pushEventTo) {
                hook.pushEventTo(target_selector, evt, payload);
              } else if (hook.pushEvent) {
                hook.pushEvent(evt, payload);
              }
            }
          } catch (error) {
            console.error('[showOptionAction] Error sending event:', error);
          } finally {
            close();
          }
        };

        optionsHost.appendChild(btn);
      });

      existing.classList.remove('hidden');
      existing.onclick = (event) => {
        if (event.target === existing || event.target === scrim) close();
      };
      cancelBtn.onclick = () => close();
      return;
    }
  }
  if (existing) existing.remove();

  const buttons = (options || []).map((opt, idx) => {
    const label = opt.label || `Opção ${idx + 1}`;
    return `<button class="w-full px-5 py-3 rounded-xl text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors option-btn" data-event="${opt.event || ''}" data-payload='${JSON.stringify(opt.data || {})}'>${label}</button>`;
  }).join('<div class="h-2"></div>');

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[9999]">
      <div class="w-full sm:w-auto sm:min-w-[420px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-emerald-100 p-5 sm:p-6">
        <div class="mb-3 sm:mb-4">
          <h3 class="text-lg font-extrabold text-emerald-900">${title}</h3>
          ${text ? `<p class="text-sm text-slate-600 mt-1">${text}</p>` : ''}
        </div>
        <div class="space-y-2">
          ${buttons}
          <button class="w-full px-5 py-3 rounded-xl text-base font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 transition-colors cancel-btn">Cancelar</button>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const close = () => {
    if (overlay) {
      overlay.remove();
    }
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('.cancel-btn')?.addEventListener('click', close);
  overlay.querySelectorAll('.option-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      try {
        const evt = btn.getAttribute('data-event');
        const payload = JSON.parse(btn.getAttribute('data-payload') || '{}');

        if (evt) {
          if (target_selector && hook.pushEventTo) {
            hook.pushEventTo(target_selector, evt, payload || {});
          } else if (hook.pushEvent) {
            hook.pushEvent(evt, payload || {});
          }
        }
      } catch (error) {
        console.error('[showOptionAction] Error sending event:', error);
      } finally {
        close();
      }
    });
  });
};

export const showConfirmAlert = (hook, data) => {
  const {
    title,
    text,
    icon = 'warning',
    confirm_text = 'Confirmar',
    cancel_text = 'Cancelar',
    reply_event = null,
    target_selector = null,
    data: payload = {}
  } = data || {};

  const staticOverlay = document.getElementById('kiosk-confirm-action-modal');
  if (staticOverlay) {
    const iconWrap = staticOverlay.querySelector('[data-role="icon-wrap"]');
    const iconSvg = staticOverlay.querySelector('[data-role="icon-svg"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const textEl = staticOverlay.querySelector('[data-role="text"]');
    const cancelBtn = staticOverlay.querySelector('[data-role="cancel"]');
    const confirmBtn = staticOverlay.querySelector('[data-role="confirm"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (iconWrap && iconSvg && titleEl && textEl && cancelBtn && confirmBtn && scrim) {
      const bgClass = icon === 'warning' ? 'bg-amber-100' : 'bg-blue-100';
      const textClass = icon === 'warning' ? 'text-amber-600' : 'text-blue-600';
      const close = () => {
        staticOverlay.classList.add('hidden');
        syncKioskInterfaceControls(hook);
      };

      iconWrap.className = `w-10 h-10 rounded-full ${bgClass} flex items-center justify-center`;
      iconSvg.className = `w-6 h-6 ${textClass}`;
      titleEl.textContent = title || 'Confirmar ação';
      textEl.textContent = text || '';
      cancelBtn.textContent = cancel_text;
      confirmBtn.textContent = confirm_text;

      staticOverlay.classList.remove('hidden');
      syncKioskInterfaceControls(hook);

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };

      cancelBtn.onclick = () => close();
      confirmBtn.onclick = () => {
        try {
          if (reply_event && target_selector && hook.pushEventTo) {
            hook.pushEventTo(target_selector, reply_event, payload || {});
          } else if (reply_event && hook.pushEvent) {
            hook.pushEvent(reply_event, payload || {});
          }
        } finally {
          close();
        }
      };

      return;
    }
  }

  const overlayId = 'validation-alert';
  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const bgClass = icon === 'warning' ? 'bg-amber-100' : 'bg-blue-100';
  const textClass = icon === 'warning' ? 'text-amber-600' : 'text-blue-600';

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[9999]" style="z-index: 99999;">
      <div class="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 transform transition-all">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full ${bgClass} flex items-center justify-center">
            <svg class="w-6 h-6 ${textClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h3 class="ml-4 text-lg font-semibold text-gray-900">${title || 'Confirmar ação'}</h3>
        </div>
        <div class="mb-6"><p class="text-sm text-gray-600">${text || ''}</p></div>
        <div class="flex justify-end gap-3">
          <button id="confirm-cancel-btn" class="px-5 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition-colors">${cancel_text}</button>
          <button id="confirm-ok-btn" class="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors">${confirm_text}</button>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const close = () => overlay && overlay.remove();
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('#confirm-cancel-btn').addEventListener('click', close);
  overlay.querySelector('#confirm-ok-btn').addEventListener('click', () => {
    try {
      if (reply_event && target_selector && hook.pushEventTo) {
        hook.pushEventTo(target_selector, reply_event, payload || {});
      } else if (reply_event && hook.pushEvent) {
        hook.pushEvent(reply_event, payload || {});
      }
    } finally {
      close();
    }
  });
};

const sendKioskTargetedEvent = (hook, targetSelector, eventName, payload = {}) => {
  if (!eventName) return;

  try {
    if (targetSelector && hook.pushEventTo) {
      hook.pushEventTo(targetSelector, eventName, payload);
      return;
    }

    if (hook.pushEvent) {
      hook.pushEvent(eventName, payload);
    }
  } catch (error) {
    console.error('Failed to send kiosk browser-modal event:', {
      eventName,
      targetSelector,
      payload,
      error
    });
  }
};

export const showWalkInConfirmModal = (hook, data) => {
  hideKioskInterfaceControls();

  const {
    title = 'Sem reserva encontrada',
    message = 'Não encontrámos uma reserva ativa para hoje.',
    confirmText = 'Avançar com consumo sem reserva',
    cancelText = 'Voltar',
    confirmEvent = 'request_walk_in',
    cancelEvent = 'walk_in_cancel',
    target_selector = '#checkin-flow-modal'
  } = data || {};

  const overlayId = 'walk-in-confirm-modal';
  const staticOverlay = document.getElementById('walk-in-confirm-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#walk-in-modal-content');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const messageEl = staticOverlay.querySelector('[data-role="message"]');
    const cancelBtn = staticOverlay.querySelector('[data-role="cancel"]');
    const cancelLabel = staticOverlay.querySelector('[data-role="cancel-label"]');
    const confirmBtn = staticOverlay.querySelector('[data-role="confirm"]');
    const confirmLabel = staticOverlay.querySelector('[data-role="confirm-label"]');

    if (
      modal &&
      scrim &&
      titleEl &&
      messageEl &&
      cancelBtn &&
      cancelLabel &&
      confirmBtn &&
      confirmLabel
    ) {
      const close = (sendCancelEvent = false) => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => {
          staticOverlay.classList.add('hidden');
          showKioskInterfaceControls();
        }, 300);

        if (sendCancelEvent) {
          sendKioskTargetedEvent(hook, target_selector, cancelEvent, {});
        }
      };

      titleEl.textContent = title;
      messageEl.textContent = message;
      cancelLabel.textContent = cancelText;
      confirmLabel.textContent = confirmText;

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      cancelBtn.onclick = () => close(true);
      confirmBtn.onclick = () => {
        close(false);
        sendKioskTargetedEvent(hook, target_selector, confirmEvent, {});
      };

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close(true);
        }
      };

      return;
    }
  }

  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="bg-gradient-to-br from-white/95 to-emerald-50/90 backdrop-blur-xl rounded-[2rem] shadow-2xl border-2 border-white/50 max-w-4xl w-full transform transition-all duration-500 scale-95 opacity-0" id="walk-in-modal-content">
        <div class="absolute top-0 right-0 h-40 w-40 bg-emerald-300/30 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 h-32 w-32 bg-teal-300/20 rounded-full blur-2xl"></div>

        <div class="relative px-8 py-8 md:px-10 md:py-10">
          <div class="text-center mb-6">
            <div class="inline-flex justify-center items-center w-20 h-20 rounded-[1.75rem] bg-gradient-to-br from-emerald-500 to-teal-600 shadow-xl shadow-emerald-200 mb-6 transform hover:scale-105 transition-transform duration-500">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>

            <p class="text-xs uppercase tracking-[0.3em] font-bold text-emerald-600 mb-2">Validar reserva</p>

            <h2 class="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">${title}</h2>
            <p class="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mt-2">${message}</p>

            <div class="mt-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50/80 backdrop-blur px-6 py-4 text-left flex items-start gap-4">
              <svg class="mt-0.5 h-7 w-7 shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M9.75 15.75h4.5M9 12a3 3 0 116 0c0 1.5-1.5 2.25-1.5 3h-3c0-.75-1.5-1.5-1.5-3z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1.5m6.36 1.14l-1.06 1.06M21 12h-1.5M4.5 12H3m3.7-5.3L5.64 5.64"></path>
              </svg>
              <p class="text-sm md:text-base text-emerald-900 leading-snug font-medium">
                <span class="font-bold">Nota:</span> A equipa de cozinha irá confirmar a refeição consumida quando validar o walk-in.
              </p>
            </div>
          </div>

          <div class="mt-8 pt-6 border-t-2 border-slate-100/50 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              id="walk-in-cancel-btn"
              class="sm:w-auto px-6 py-4 rounded-xl border-2 border-slate-300 bg-white/80 backdrop-blur text-slate-600 font-semibold text-base transition-all duration-300 hover:bg-slate-50 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5 flex items-center justify-center gap-2 group">
              <span class="group-hover:-translate-x-1 transition-transform">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                </svg>
              </span>
              ${cancelText}
            </button>

            <button
              id="walk-in-confirm-btn"
              class="flex-1 px-8 py-5 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 text-white font-bold text-xl shadow-xl transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(16,185,129,0.6)] hover:-translate-y-1 hover:from-emerald-600 hover:via-teal-600 hover:to-emerald-700 transform animate-pulse-subtle relative overflow-hidden group">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span class="relative flex items-center justify-center gap-3">
                <svg class="w-6 h-6 animate-bounce-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"></path>
                  <circle cx="12" cy="12" r="10" stroke-width="2" opacity="0.3"></circle>
                </svg>
                <span class="tracking-wide">${confirmText}</span>
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('walk-in-modal-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = (sendCancelEvent = false) => {
    if (modal) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        showKioskInterfaceControls();
      }, 300);

      if (sendCancelEvent) {
        sendKioskTargetedEvent(hook, target_selector, cancelEvent, {});
      }
    }
  };

  document.getElementById('walk-in-cancel-btn').addEventListener('click', () => {
    close(true);
  });

  document.getElementById('walk-in-confirm-btn').addEventListener('click', () => {
    close(false);
    sendKioskTargetedEvent(hook, target_selector, confirmEvent, {});
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      close(true);
    }
  });
};

export const showWalkInConflictModal = (hook, data) => {
  hideKioskInterfaceControls();

  const {
    title = 'Refeição já atribuída',
    message = 'Detetámos que já possui uma reserva ou consumo registado para hoje. Apenas é permitida uma refeição por dia.',
    question = 'Deseja cancelar a reserva ativa e avançar com o consumo sem reserva?',
    confirmText = 'Sim, Cancelar e Continuar',
    cancelText = 'Voltar',
    allow_cancel_resolution = true,
    existing_meal_name = '',
    existing_meal_type = '',
    existing_meal_type_emoji = '',
    existing_meal_period = '',
    existing_meal_date = '',
    existing_dining_hall = '',
    conflicting_reservation_id = null,
    target_selector = '#checkin-flow-modal'
  } = data || {};

  const overlayId = 'walk-in-conflict-modal';
  const staticOverlay = document.getElementById('walk-in-conflict-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#walk-in-conflict-content');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const messageEl = staticOverlay.querySelector('[data-role="message"]');
    const existingMealNameEl = staticOverlay.querySelector('[data-role="existing-meal-name"]');
    const existingMealTypeEmojiEl = staticOverlay.querySelector('[data-role="existing-meal-type-emoji"]');
    const existingMealTypeEl = staticOverlay.querySelector('[data-role="existing-meal-type"]');
    const existingMealPeriodEl = staticOverlay.querySelector('[data-role="existing-meal-period"]');
    const existingMealDateEl = staticOverlay.querySelector('[data-role="existing-meal-date"]');
    const existingDiningHallEl = staticOverlay.querySelector('[data-role="existing-dining-hall"]');
    const questionEl = staticOverlay.querySelector('[data-role="question"]');
    const actionsEl = staticOverlay.querySelector('[data-role="actions"]');
    const confirmBtn = staticOverlay.querySelector('[data-role="confirm"]');
    const confirmLabel = staticOverlay.querySelector('[data-role="confirm-label"]');
    const cancelBtn = staticOverlay.querySelector('[data-role="cancel"]');
    const cancelLabel = staticOverlay.querySelector('[data-role="cancel-label"]');

    if (
      modal &&
      scrim &&
      titleEl &&
      messageEl &&
      existingMealNameEl &&
      existingMealTypeEmojiEl &&
      existingMealTypeEl &&
      existingMealPeriodEl &&
      existingMealDateEl &&
      existingDiningHallEl &&
      questionEl &&
      actionsEl &&
      confirmBtn &&
      confirmLabel &&
      cancelBtn &&
      cancelLabel
    ) {
      const close = (callback = null) => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => {
          staticOverlay.classList.add('hidden');
          showKioskInterfaceControls();
          if (typeof callback === 'function') callback();
        }, 300);
      };

      titleEl.textContent = title;
      messageEl.textContent = message;
      existingMealNameEl.textContent = existing_meal_name || '—';
      setOptionalTextIcon(existingMealTypeEmojiEl, existing_meal_type_emoji);
      existingMealTypeEl.textContent = existing_meal_type || '—';
      existingMealPeriodEl.textContent = existing_meal_period || '—';
      existingMealDateEl.textContent = existing_meal_date || '—';
      existingDiningHallEl.textContent = existing_dining_hall || '—';
      questionEl.textContent = question;
      confirmLabel.textContent = confirmText;
      cancelLabel.textContent = cancelText;

      actionsEl.classList.toggle('sm:grid-cols-2', !!allow_cancel_resolution);
      confirmBtn.classList.toggle('hidden', !allow_cancel_resolution);
      cancelBtn.className = allow_cancel_resolution
        ? 'order-1 sm:order-2 bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-4 rounded-2xl text-lg font-black active:scale-95 transition-all'
        : 'order-1 bg-orange-600 text-white shadow-xl shadow-orange-200 px-6 py-4 rounded-2xl text-lg font-black active:scale-95 transition-all';

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      if (allow_cancel_resolution) {
        confirmBtn.onclick = () => {
          close(() => {
            sendKioskTargetedEvent(hook, target_selector, 'resolve_walk_in_conflict', {
              id: conflicting_reservation_id
            });
          });
        };
      } else {
        confirmBtn.onclick = null;
      }

      cancelBtn.onclick = () => {
        close(() => {
          sendKioskTargetedEvent(hook, target_selector, 'close_conflict_modal', {});
        });
      };

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close(() => {
            sendKioskTargetedEvent(hook, target_selector, 'close_conflict_modal', {});
          });
        }
      };

      return;
    }
  }

  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="w-full max-w-[42rem] bg-white/95 rounded-[2.5rem] shadow-[0_32px_80px_-18px_rgba(0,0,0,0.38)] overflow-hidden border border-white/50 transform transition-all duration-500 scale-95 opacity-0" id="walk-in-conflict-content">
        <div class="py-8 text-center space-y-6 px-6">
          <div class="flex justify-center">
            <div class="relative h-20 w-20 flex items-center justify-center rounded-[1.75rem] bg-gradient-to-br from-orange-400 to-red-600 shadow-xl shadow-orange-200">
              <svg class="h-10 w-10 text-white drop-shadow-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div class="absolute inset-0 rounded-[2.5rem] bg-white/20 animate-pulse"></div>
            </div>
          </div>

          <div class="space-y-5">
            <div class="space-y-2">
              <p class="text-sm font-black text-orange-600 uppercase tracking-[0.3em]">Conflito Identificado</p>
              <h2 class="text-[2rem] md:text-[2.35rem] font-black text-slate-900 tracking-tight leading-tight">${title}</h2>
            </div>

            <p class="text-lg text-slate-500 font-semibold leading-relaxed max-w-xl mx-auto">${message}</p>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl mx-auto text-left">
              <div class="rounded-[1.35rem] border border-orange-100 bg-white/70 px-4 py-3">
                <p class="text-xs font-black uppercase tracking-[0.25em] text-orange-500">Refeição</p>
                <p class="mt-2 text-lg font-black text-slate-900">${existing_meal_name || '—'}</p>
                <p class="mt-1 inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-sm font-bold text-orange-700">
                  <svg class="h-4 w-4 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M4 3v7a4 4 0 0 0 8 0V3" />
                    <path d="M8 3v18" />
                    <path d="M16 3v18" />
                    <path d="M20 3v5a4 4 0 0 1-4 4" />
                  </svg>
                  <span>${existing_meal_type || '—'}</span>
                </p>
              </div>
              <div class="rounded-[1.35rem] border border-orange-100 bg-white/70 px-4 py-3">
                <p class="text-xs font-black uppercase tracking-[0.25em] text-orange-500">Período</p>
                <p class="mt-2 text-lg font-black text-slate-900">${existing_meal_period || '—'}</p>
              </div>
              <div class="rounded-[1.35rem] border border-orange-100 bg-white/70 px-4 py-3">
                <p class="text-xs font-black uppercase tracking-[0.25em] text-orange-500">Data</p>
                <p class="mt-2 text-lg font-black text-slate-900">${existing_meal_date || '—'}</p>
              </div>
              <div class="rounded-[1.35rem] border border-orange-100 bg-white/70 px-4 py-3">
                <p class="text-xs font-black uppercase tracking-[0.25em] text-orange-500">Refeitório</p>
                <p class="mt-2 text-lg font-black text-slate-900">${existing_dining_hall || '—'}</p>
              </div>
            </div>

            <div class="relative py-4 px-5 bg-gradient-to-br from-orange-50/50 to-orange-100/30 rounded-[1.7rem] border border-orange-100/50 inline-block overflow-hidden group">
              <div class="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span class="text-5xl italic font-black">?</span>
              </div>
              <p class="relative z-10 text-base font-bold text-orange-950 leading-snug">${question}</p>
            </div>
          </div>

          <div class="grid grid-cols-1 ${allow_cancel_resolution ? 'sm:grid-cols-2' : ''} gap-4 pt-2 px-6 sm:px-10">
            ${allow_cancel_resolution ? `
            <button id="walk-in-conflict-confirm-btn" class="group order-2 sm:order-1 relative overflow-hidden px-6 py-4 rounded-2xl bg-orange-600 text-white shadow-xl shadow-orange-200 transition-all active:scale-95">
              <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              <span class="relative text-lg font-black tracking-tight">${confirmText}</span>
            </button>` : ''}

            <button id="walk-in-conflict-cancel-btn" class="${allow_cancel_resolution ? 'order-1 sm:order-2 bg-slate-100 text-slate-700 hover:bg-slate-200' : 'order-1 bg-orange-600 text-white shadow-xl shadow-orange-200'} px-6 py-4 rounded-2xl text-lg font-black active:scale-95 transition-all">
              ${cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('walk-in-conflict-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = (callback = null) => {
    if (modal) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        showKioskInterfaceControls();
        if (typeof callback === 'function') callback();
      }, 300);
    }
  };

  const confirmButton = document.getElementById('walk-in-conflict-confirm-btn');
  if (confirmButton) {
    confirmButton.addEventListener('click', () => {
      close(() => {
        sendKioskTargetedEvent(hook, target_selector, 'resolve_walk_in_conflict', { id: conflicting_reservation_id });
      });
    });
  }

  document.getElementById('walk-in-conflict-cancel-btn').addEventListener('click', () => {
    close(() => {
      sendKioskTargetedEvent(hook, target_selector, 'close_conflict_modal', {});
    });
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      close(() => {
        sendKioskTargetedEvent(hook, target_selector, 'close_conflict_modal', {});
      });
    }
  });
};

export const showWalkInSuccessModal = (_hook, data) => {
  const {
    title = 'Operação concluída',
    message = '',
    dining_hall = '',
    meal_period = '',
    meal_date = ''
  } = data || {};

  const staticOverlay = document.getElementById('walk-in-success-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#walk-in-success-content');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const messageEl = staticOverlay.querySelector('[data-role="message"]');
    const detailsEl = staticOverlay.querySelector('[data-role="details"]');
    const diningHallEl = staticOverlay.querySelector('[data-role="dining-hall"]');
    const mealPeriodEl = staticOverlay.querySelector('[data-role="meal-period"]');
    const mealDateEl = staticOverlay.querySelector('[data-role="meal-date"]');
    const detailsDiningHall = staticOverlay.querySelector('[data-role="details-dining-hall"]');
    const detailsMealPeriod = staticOverlay.querySelector('[data-role="details-meal-period"]');
    const detailsMealDate = staticOverlay.querySelector('[data-role="details-meal-date"]');
    const okBtn = staticOverlay.querySelector('[data-role="ok"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (
      modal &&
      titleEl &&
      messageEl &&
      detailsEl &&
      diningHallEl &&
      mealPeriodEl &&
      mealDateEl &&
      detailsDiningHall &&
      detailsMealPeriod &&
      detailsMealDate &&
      okBtn &&
      scrim
    ) {
      const close = () => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => staticOverlay.classList.add('hidden'), 300);
      };

      titleEl.textContent = title;
      messageEl.textContent = message;

      const hasDiningHall = !!dining_hall;
      const hasMealPeriod = !!meal_period;
      const hasMealDate = !!meal_date;
      const hasDetails = hasDiningHall || hasMealPeriod || hasMealDate;

      detailsEl.classList.toggle('hidden', !hasDetails);
      detailsDiningHall.classList.toggle('hidden', !hasDiningHall);
      detailsMealPeriod.classList.toggle('hidden', !hasMealPeriod);
      detailsMealDate.classList.toggle('hidden', !hasMealDate);

      diningHallEl.textContent = dining_hall;
      mealPeriodEl.textContent = meal_period;
      mealDateEl.textContent = meal_date;

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };
      okBtn.onclick = () => close();
      setTimeout(() => {
        if (!staticOverlay.classList.contains('hidden')) {
          close();
        }
      }, 4000);

      return;
    }
  }

  const overlayId = 'walk-in-success-modal';
  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  let detailsHtml = '';
  if (dining_hall || meal_period || meal_date) {
    const details = [];
    if (dining_hall) details.push(`<div class="flex items-center gap-3"><svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg><span class="text-slate-700 font-medium">${dining_hall}</span></div>`);
    if (meal_period) details.push(`<div class="flex items-center gap-3"><svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg><span class="text-slate-700 font-medium">${meal_period}</span></div>`);
    if (meal_date) details.push(`<div class="flex items-center gap-3"><svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><span class="text-slate-700 font-medium">${meal_date}</span></div>`);
    detailsHtml = `<div class="mt-8 rounded-2xl border-2 border-emerald-200 bg-emerald-50/80 backdrop-blur px-8 py-6 space-y-3">${details.join('')}</div>`;
  }

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="bg-gradient-to-br from-white/95 to-emerald-50/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border-2 border-white/50 max-w-4xl w-full transform transition-all duration-500 scale-95 opacity-0" id="walk-in-success-content">
        <div class="absolute top-0 right-0 h-48 w-48 bg-emerald-300/30 rounded-full blur-3xl animate-pulse"></div>
        <div class="absolute bottom-0 left-0 h-40 w-40 bg-teal-300/20 rounded-full blur-2xl animate-pulse"></div>

        <div class="relative p-12 md:p-16">
          <div class="flex justify-center mb-10">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-2xl opacity-60 animate-pulse"></div>
              <div class="relative w-32 h-32 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-2xl animate-bounce-success">
                <svg class="w-20 h-20 md:w-24 md:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
          </div>

          <div class="text-center space-y-5 mb-12">
            <p class="text-sm uppercase tracking-[0.30em] text-emerald-600 font-bold animate-fade-in">Sucesso</p>
            <h2 class="text-4xl md:text-5xl font-black text-slate-900 leading-tight animate-fade-in-up">${title}</h2>
            <p class="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-2xl mx-auto animate-fade-in-up-delay">${message}</p>
            ${detailsHtml}
          </div>

          <div class="flex justify-center">
            <button
              id="walk-in-success-btn"
              class="px-16 py-7 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 text-white font-bold text-2xl shadow-2xl transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(16,185,129,0.6)] hover:-translate-y-1 hover:from-emerald-600 hover:via-teal-600 hover:to-emerald-700 transform animate-pulse-subtle relative overflow-hidden group">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span class="relative flex items-center justify-center gap-4">
                <span class="tracking-wide">OK</span>
                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('walk-in-success-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = () => {
    if (modal) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => overlay.remove(), 300);
    }
  };

  document.getElementById('walk-in-success-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  setTimeout(close, 4000);
};

export const showCheckinSuccessModal = (_hook, data) => {
  const {
    title = 'Check-in confirmado',
    message = 'Dirija-se ao balcão para levantar a refeição.',
    meal_name = '',
    meal_type_name = '',
    meal_type_emoji = '',
    dining_hall = '',
    meal_period = '',
    meal_date = ''
  } = data || {};

  const overlayId = 'checkin-success-modal';

  const colors = {
    gradient: 'from-emerald-500 via-teal-500 to-emerald-600',
    blur: 'bg-emerald-400/40',
    border: 'border-emerald-200',
    textAccent: 'text-emerald-600',
    buttonGradient: 'from-emerald-500 via-teal-500 to-emerald-600',
    buttonHover: 'hover:from-emerald-600 hover:via-teal-600 hover:to-emerald-700',
    shadowColor: 'rgba(16,185,129,0.6)'
  };
  const infoItems = [
    { label: 'Refeição', value: meal_name },
    { label: 'Tipo', value: meal_type_name },
    { label: 'Refeitório', value: dining_hall },
    { label: 'Período', value: meal_period },
    { label: 'Data', value: meal_date }
  ].filter(({ value }) => !!value);
  const renderInfoCardHtml = ({ label, value }) => `
    <div class="bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-4 border-2 ${colors.border} shadow-lg transform hover:scale-105 transition-all duration-300">
      <div class="text-xs uppercase tracking-widest ${colors.textAccent} font-bold mb-1">${label}</div>
      <div class="text-lg font-bold text-slate-800">${value}</div>
    </div>
  `;

  const staticOverlay = document.getElementById('checkin-success-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#checkin-success-content');
    const blurPrimary = staticOverlay.querySelector('[data-role="blur-primary"]');
    const blurSecondary = staticOverlay.querySelector('[data-role="blur-secondary"]');
    const iconGlow = staticOverlay.querySelector('[data-role="icon-glow"]');
    const iconFrame = staticOverlay.querySelector('[data-role="icon-frame"]');
    const emojiEl = staticOverlay.querySelector('[data-role="emoji"]');
    const badge = staticOverlay.querySelector('[data-role="badge"]');
    const sparkA = staticOverlay.querySelector('[data-role="spark-a"]');
    const sparkB = staticOverlay.querySelector('[data-role="spark-b"]');
    const sparkC = staticOverlay.querySelector('[data-role="spark-c"]');
    const eyebrow = staticOverlay.querySelector('[data-role="eyebrow"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const messageEl = staticOverlay.querySelector('[data-role="message"]');
    const infoCardsEl = staticOverlay.querySelector('[data-role="info-cards"]');
    const okBtn = staticOverlay.querySelector('[data-role="ok"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (
      modal &&
      blurPrimary &&
      blurSecondary &&
      iconGlow &&
      iconFrame &&
      emojiEl &&
      badge &&
      sparkA &&
      sparkB &&
      sparkC &&
      eyebrow &&
      titleEl &&
      messageEl &&
      infoCardsEl &&
      okBtn &&
      scrim
    ) {
      const close = () => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => staticOverlay.classList.add('hidden'), 300);
      };

      blurPrimary.className = `absolute top-0 right-0 h-32 w-32 ${colors.blur} rounded-full blur-3xl animate-pulse`;
      blurSecondary.className = `absolute bottom-0 left-0 h-24 w-24 ${colors.blur} rounded-full blur-3xl animate-pulse`;
      iconGlow.className = `absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-full blur-2xl opacity-50 animate-pulse scale-110`;
      iconFrame.className = `relative w-32 h-32 rounded-full bg-gradient-to-br from-white/90 to-slate-50/80 flex items-center justify-center shadow-2xl border-4 ${colors.border} transform hover:scale-105 transition-all duration-500`;
      badge.className = `absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-2xl border-4 border-white animate-bounce-success`;
      sparkA.className = `absolute top-1/4 -left-4 w-2 h-2 ${colors.blur} rounded-full animate-ping`;
      sparkB.className = `absolute top-1/2 -right-3 w-2 h-2 ${colors.blur} rounded-full animate-ping`;
      sparkC.className = `absolute bottom-1/4 left-1 w-1 h-1 ${colors.blur} rounded-full animate-ping`;
      eyebrow.className = `text-xs uppercase tracking-wider ${colors.textAccent} font-black animate-fade-in`;
      okBtn.className = `px-12 py-4 rounded-xl bg-gradient-to-r ${colors.buttonGradient} text-white font-black text-xl shadow-2xl transition-all duration-500 hover:shadow-[0_25px_70px_-20px_${colors.shadowColor}] hover:-translate-y-1 ${colors.buttonHover} transform animate-pulse-subtle relative overflow-hidden group border-2 border-white/20`;

      setOptionalTextIcon(emojiEl, meal_type_emoji);
      titleEl.textContent = title;
      messageEl.textContent = message;

      infoCardsEl.classList.toggle('hidden', infoItems.length === 0);
      populateDetailCards(infoCardsEl, infoItems, {
        cardClass: `bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-4 border-2 ${colors.border} shadow-lg transform hover:scale-105 transition-all duration-300`,
        labelClass: `text-xs uppercase tracking-widest ${colors.textAccent} font-bold mb-1`,
        valueClass: 'text-lg font-bold text-slate-800'
      });

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };
      okBtn.onclick = () => close();
      setTimeout(() => {
        if (!staticOverlay.classList.contains('hidden')) {
          close();
        }
      }, 4000);

      return;
    }
  }

  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const infoCardsHtml = infoItems.length > 0
    ? `<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 mb-8">${infoItems.map(renderInfoCardHtml).join('')}</div>`
    : '';

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="bg-gradient-to-br from-white/95 to-slate-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/50 max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-500 scale-95 opacity-0" id="checkin-success-content">
        <div class="absolute top-0 right-0 h-32 w-32 ${colors.blur} rounded-full blur-3xl animate-pulse"></div>
        <div class="absolute bottom-0 left-0 h-24 w-24 ${colors.blur} rounded-full blur-3xl animate-pulse" style="animation-delay: 0.5s"></div>

        <div class="relative p-6">
          <div class="flex justify-center mb-4">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-full blur-2xl opacity-50 animate-pulse scale-110"></div>
              <div class="relative w-32 h-32 rounded-full bg-gradient-to-br from-white/90 to-slate-50/80 flex items-center justify-center shadow-2xl border-4 ${colors.border} transform hover:scale-105 transition-all duration-500">
                <svg class="h-16 w-16 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <div class="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-2xl border-4 border-white animate-bounce-success">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3.5" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              </div>

              <div class="absolute top-1/4 -left-4 w-2 h-2 ${colors.blur} rounded-full animate-ping" style="animation-duration: 2s"></div>
              <div class="absolute top-1/2 -right-3 w-2 h-2 ${colors.blur} rounded-full animate-ping" style="animation-duration: 2.5s; animation-delay: 0.3s"></div>
              <div class="absolute bottom-1/4 left-1 w-1 h-1 ${colors.blur} rounded-full animate-ping" style="animation-duration: 3s; animation-delay: 0.6s"></div>
            </div>
          </div>

          <div class="text-center space-y-2 mb-4">
            <p class="text-xs uppercase tracking-wider ${colors.textAccent} font-black animate-fade-in">Check-in confirmado</p>
            <h2 class="text-3xl font-black text-slate-900 leading-tight animate-fade-in-up tracking-tight">${title}</h2>
            <p class="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto animate-fade-in-up-delay font-medium">${message}</p>
          </div>

          ${infoCardsHtml}

          <div class="flex justify-center mt-6">
            <button
              id="checkin-success-btn"
              class="px-12 py-4 rounded-xl bg-gradient-to-r ${colors.buttonGradient} text-white font-black text-xl shadow-2xl transition-all duration-500 hover:shadow-[0_25px_70px_-20px_${colors.shadowColor}] hover:-translate-y-1 ${colors.buttonHover} transform animate-pulse-subtle relative overflow-hidden group border-2 border-white/20">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out"></div>
              <div class="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/10 transition-all duration-300"></div>
              <span class="relative flex items-center justify-center gap-3">
                <span class="tracking-wider">ENTENDIDO</span>
                <svg class="w-6 h-6 transform group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <style>
      @keyframes bounce-gentle {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @keyframes bounce-success {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes fade-in-up {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes fade-in-up-delay {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes pulse-subtle {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.02); }
      }
      .animate-bounce-gentle { animation: bounce-gentle 3s ease-in-out infinite; }
      .animate-bounce-success { animation: bounce-success 2s ease-in-out infinite; }
      .animate-fade-in { animation: fade-in 0.6s ease-out; }
      .animate-fade-in-up { animation: fade-in-up 0.8s ease-out 0.2s both; }
      .animate-fade-in-up-delay { animation: fade-in-up-delay 1s ease-out 0.4s both; }
      .animate-pulse-subtle { animation: pulse-subtle 3s ease-in-out infinite; }
    </style>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('checkin-success-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = () => {
    if (modal) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => overlay.remove(), 300);
    }
  };

  document.getElementById('checkin-success-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  setTimeout(close, 4000);
};

export const showAlreadyCheckedInModal = (hook, data) => {
  hideKioskInterfaceControls();
  closeKnownKioskModals(hook, ['checkin-confirm-modal'], { sync: false });
  const {
    meal_name = '',
    meal_type_emoji = '',
    dining_hall = '',
    meal_period = '',
    meal_date = '',
    title = 'Já efetuou check-in',
    message = 'O check-in desta refeição já foi registado.',
    secondary_message = 'Não é necessária nova validação.',
    eyebrow = 'Informação',
    target_selector = '#checkin-flow-modal'
  } = data || {};

  const overlayId = 'already-checked-in-modal';
  const colors = {
    gradient: 'from-blue-500 via-indigo-500 to-blue-600',
    blur: 'bg-blue-400/40',
    border: 'border-blue-200',
    textAccent: 'text-blue-600',
    buttonGradient: 'from-blue-500 via-indigo-500 to-blue-600',
    buttonHover: 'hover:from-blue-600 hover:via-indigo-600 hover:to-blue-700'
  };

  const infoItems = [
    { label: 'Refeição', value: meal_name },
    { label: 'Refeitório', value: dining_hall },
    { label: 'Período', value: meal_period },
    { label: 'Data', value: meal_date }
  ].filter(({ value }) => !!value);
  const renderInfoCardHtml = ({ label, value }) => `
    <div class="bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-4 border-2 ${colors.border} shadow-lg">
      <div class="text-xs uppercase tracking-widest ${colors.textAccent} font-bold mb-1">${label}</div>
      <div class="text-lg font-bold text-slate-800">${value}</div>
    </div>
  `;

  const staticOverlay = document.getElementById('already-checked-in-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#already-checked-in-content');
    const blurPrimary = staticOverlay.querySelector('[data-role="blur-primary"]');
    const blurSecondary = staticOverlay.querySelector('[data-role="blur-secondary"]');
    const iconGlow = staticOverlay.querySelector('[data-role="icon-glow"]');
    const iconFrame = staticOverlay.querySelector('[data-role="icon-frame"]');
    const emojiEl = staticOverlay.querySelector('[data-role="emoji"]');
    const badge = staticOverlay.querySelector('[data-role="badge"]');
    const eyebrow = staticOverlay.querySelector('[data-role="eyebrow"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const messageEl = staticOverlay.querySelector('[data-role="message"]');
    const infoCardsEl = staticOverlay.querySelector('[data-role="info-cards"]');
    const okBtn = staticOverlay.querySelector('[data-role="ok"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (
      modal &&
      blurPrimary &&
      blurSecondary &&
      iconGlow &&
      iconFrame &&
      emojiEl &&
      badge &&
      eyebrow &&
      titleEl &&
      messageEl &&
      infoCardsEl &&
      okBtn &&
      scrim
    ) {
      let closeEventSent = false;
      const sendCloseEvent = () => {
        if (closeEventSent) return;
        closeEventSent = true;
        sendKioskTargetedEvent(hook, target_selector, 'close_modal', {});
      };

      const close = () => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => {
          staticOverlay.classList.add('hidden');
          showKioskInterfaceControls();
        }, 300);
        sendCloseEvent();
      };

      blurPrimary.className = `absolute top-0 right-0 h-32 w-32 ${colors.blur} rounded-full blur-3xl animate-pulse`;
      blurSecondary.className = `absolute bottom-0 left-0 h-24 w-24 ${colors.blur} rounded-full blur-3xl animate-pulse`;
      iconGlow.className = `absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-full blur-2xl opacity-50 animate-pulse scale-110`;
      iconFrame.className = `relative w-32 h-32 rounded-full bg-gradient-to-br from-white/90 to-slate-50/80 flex items-center justify-center shadow-2xl border-4 ${colors.border}`;
      badge.className = `absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-2xl border-4 border-white`;
      eyebrow.className = `text-xs uppercase tracking-wider ${colors.textAccent} font-black`;

      setOptionalTextIcon(emojiEl, meal_type_emoji);
      titleEl.textContent = title;
      setRichMessage(
        messageEl,
        message,
        secondary_message
      );
      infoCardsEl.classList.toggle('hidden', infoItems.length === 0);
      populateDetailCards(infoCardsEl, infoItems, {
        cardClass: `bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-4 border-2 ${colors.border} shadow-lg`,
        labelClass: `text-xs uppercase tracking-widest ${colors.textAccent} font-bold mb-1`,
        valueClass: 'text-lg font-bold text-slate-800'
      });

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };
      okBtn.onclick = () => close();
      setTimeout(close, 5000);

      return;
    }
  }

  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const infoCardsHtml = infoItems.length > 0
    ? `<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 mb-8">${infoItems.map(renderInfoCardHtml).join('')}</div>`
    : '';

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="bg-gradient-to-br from-white/95 to-slate-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/50 max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-500 scale-95 opacity-0" id="already-checked-in-content">
        <div class="absolute top-0 right-0 h-32 w-32 ${colors.blur} rounded-full blur-3xl animate-pulse"></div>
        <div class="absolute bottom-0 left-0 h-24 w-24 ${colors.blur} rounded-full blur-3xl animate-pulse" style="animation-delay: 0.5s"></div>

        <div class="relative p-6">
          <div class="flex justify-center mb-4">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-full blur-2xl opacity-50 animate-pulse scale-110"></div>
              <div class="relative w-32 h-32 rounded-full bg-gradient-to-br from-white/90 to-slate-50/80 flex items-center justify-center shadow-2xl border-4 ${colors.border}">
                <svg class="h-16 w-16 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3"></path>
                  <circle cx="12" cy="12" r="9" stroke-width="2"></circle>
                </svg>
                <div class="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-2xl border-4 border-white">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 8v4l3 3"></path>
                    <circle cx="12" cy="12" r="9" stroke-width="3"></circle>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="text-center space-y-2 mb-4">
            <p class="text-xs uppercase tracking-wider ${colors.textAccent} font-black">Informação</p>
            <h2 class="text-3xl font-black text-slate-900 leading-tight tracking-tight">Já efetuou check-in</h2>
            <p class="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto font-medium">
              O check-in desta refeição já foi registado.<br/>
              <span class="text-slate-500">Não é necessária nova validação.</span>
            </p>
          </div>

          ${infoCardsHtml}

          <div class="flex justify-center mt-6">
            <button
              id="already-checked-in-btn"
              class="px-12 py-4 rounded-xl bg-gradient-to-r ${colors.buttonGradient} text-white font-black text-xl shadow-2xl transition-all duration-500 hover:-translate-y-1 ${colors.buttonHover} transform relative overflow-hidden group border-2 border-white/20">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              <span class="relative flex items-center justify-center gap-3">
                <span class="tracking-wider">ENTENDIDO</span>
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('already-checked-in-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  let closeEventSent = false;
  const sendCloseEvent = () => {
    if (closeEventSent) return;
    closeEventSent = true;
    sendKioskTargetedEvent(hook, target_selector, 'close_modal', {});
  };

  const close = () => {
    if (modal) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        showKioskInterfaceControls();
      }, 300);
    }

    sendCloseEvent();
  };

  document.getElementById('already-checked-in-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  setTimeout(close, 5000);
};

export const showAlreadyServedModal = (hook, data) => {
  hideKioskInterfaceControls();
  closeKnownKioskModals(hook, ['checkin-confirm-modal'], { sync: false });
  const {
    meal_name = '',
    meal_type_emoji = '',
    dining_hall = '',
    meal_period = '',
    meal_date = '',
    title = 'Refeição já consumida',
    message = 'Esta refeição já foi servida e consumida.',
    secondary_message = 'Não é possível fazer novo check-in.',
    eyebrow = 'Aviso',
    target_selector = '#checkin-flow-modal'
  } = data || {};

  const overlayId = 'already-served-modal';
  const colors = {
    gradient: 'from-amber-500 via-orange-500 to-amber-600',
    blur: 'bg-amber-400/40',
    border: 'border-amber-200',
    textAccent: 'text-amber-600',
    buttonGradient: 'from-amber-500 via-orange-500 to-amber-600',
    buttonHover: 'hover:from-amber-600 hover:via-orange-600 hover:to-amber-700'
  };

  const infoItems = [
    { label: 'Refeição', value: meal_name },
    { label: 'Refeitório', value: dining_hall },
    { label: 'Período', value: meal_period },
    { label: 'Data', value: meal_date }
  ].filter(({ value }) => !!value);
  const renderInfoCardHtml = ({ label, value }) => `
    <div class="bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-4 border-2 ${colors.border} shadow-lg">
      <div class="text-xs uppercase tracking-widest ${colors.textAccent} font-bold mb-1">${label}</div>
      <div class="text-lg font-bold text-slate-800">${value}</div>
    </div>
  `;

  const staticOverlay = document.getElementById('already-served-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#already-served-content');
    const blurPrimary = staticOverlay.querySelector('[data-role="blur-primary"]');
    const blurSecondary = staticOverlay.querySelector('[data-role="blur-secondary"]');
    const iconGlow = staticOverlay.querySelector('[data-role="icon-glow"]');
    const iconFrame = staticOverlay.querySelector('[data-role="icon-frame"]');
    const emojiEl = staticOverlay.querySelector('[data-role="emoji"]');
    const badge = staticOverlay.querySelector('[data-role="badge"]');
    const eyebrow = staticOverlay.querySelector('[data-role="eyebrow"]');
    const titleEl = staticOverlay.querySelector('[data-role="title"]');
    const messageEl = staticOverlay.querySelector('[data-role="message"]');
    const infoCardsEl = staticOverlay.querySelector('[data-role="info-cards"]');
    const okBtn = staticOverlay.querySelector('[data-role="ok"]');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');

    if (
      modal &&
      blurPrimary &&
      blurSecondary &&
      iconGlow &&
      iconFrame &&
      emojiEl &&
      badge &&
      eyebrow &&
      titleEl &&
      messageEl &&
      infoCardsEl &&
      okBtn &&
      scrim
    ) {
      let closeEventSent = false;
      const sendCloseEvent = () => {
        if (closeEventSent) return;
        closeEventSent = true;
        sendKioskTargetedEvent(hook, target_selector, 'close_modal', {});
      };

      const close = () => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => {
          staticOverlay.classList.add('hidden');
          showKioskInterfaceControls();
        }, 300);
        sendCloseEvent();
      };

      blurPrimary.className = `absolute top-0 right-0 h-32 w-32 ${colors.blur} rounded-full blur-3xl animate-pulse`;
      blurSecondary.className = `absolute bottom-0 left-0 h-24 w-24 ${colors.blur} rounded-full blur-3xl animate-pulse`;
      iconGlow.className = `absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-full blur-2xl opacity-50 animate-pulse scale-110`;
      iconFrame.className = `relative w-32 h-32 rounded-full bg-gradient-to-br from-white/90 to-slate-50/80 flex items-center justify-center shadow-2xl border-4 ${colors.border}`;
      badge.className = `absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-2xl border-4 border-white`;
      eyebrow.className = `text-xs uppercase tracking-wider ${colors.textAccent} font-black`;

      setOptionalTextIcon(emojiEl, meal_type_emoji);
      titleEl.textContent = title;
      setRichMessage(
        messageEl,
        message,
        secondary_message
      );
      infoCardsEl.classList.toggle('hidden', infoItems.length === 0);
      populateDetailCards(infoCardsEl, infoItems, {
        cardClass: `bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-4 border-2 ${colors.border} shadow-lg`,
        labelClass: `text-xs uppercase tracking-widest ${colors.textAccent} font-bold mb-1`,
        valueClass: 'text-lg font-bold text-slate-800'
      });

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
        }
      };
      okBtn.onclick = () => close();
      setTimeout(close, 5000);

      return;
    }
  }

  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const infoCardsHtml = infoItems.length > 0
    ? `<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 mb-8">${infoItems.map(renderInfoCardHtml).join('')}</div>`
    : '';

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="bg-gradient-to-br from-white/95 to-slate-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/50 max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-500 scale-95 opacity-0" id="already-served-content">
        <div class="absolute top-0 right-0 h-32 w-32 ${colors.blur} rounded-full blur-3xl animate-pulse"></div>
        <div class="absolute bottom-0 left-0 h-24 w-24 ${colors.blur} rounded-full blur-3xl animate-pulse" style="animation-delay: 0.5s"></div>

        <div class="relative p-6">
          <div class="flex justify-center mb-4">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-full blur-2xl opacity-50 animate-pulse scale-110"></div>
              <div class="relative w-32 h-32 rounded-full bg-gradient-to-br from-white/90 to-slate-50/80 flex items-center justify-center shadow-2xl border-4 ${colors.border}">
                <svg class="h-16 w-16 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
                </svg>
                <div class="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-2xl border-4 border-white">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="text-center space-y-2 mb-4">
            <p class="text-xs uppercase tracking-wider ${colors.textAccent} font-black">${eyebrow}</p>
            <h2 class="text-3xl font-black text-slate-900 leading-tight tracking-tight">${title}</h2>
            <p class="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto font-medium">
              ${message}<br/>
              <span class="text-slate-500">${secondary_message}</span>
            </p>
          </div>

          ${infoCardsHtml}

          <div class="flex justify-center mt-6">
            <button
              id="already-served-btn"
              class="px-12 py-4 rounded-xl bg-gradient-to-r ${colors.buttonGradient} text-white font-black text-xl shadow-2xl transition-all duration-500 hover:-translate-y-1 ${colors.buttonHover} transform relative overflow-hidden group border-2 border-white/20">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              <span class="relative flex items-center justify-center gap-3">
                <span class="tracking-wider">ENTENDIDO</span>
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('already-served-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  let closeEventSent = false;
  const sendCloseEvent = () => {
    if (closeEventSent) return;
    closeEventSent = true;
    sendKioskTargetedEvent(hook, target_selector, 'close_modal', {});
  };

  const close = () => {
    if (modal) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        showKioskInterfaceControls();
      }, 300);
    }

    sendCloseEvent();
  };

  document.getElementById('already-served-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  setTimeout(close, 5000);
};

export const showCheckinConfirmModal = (hook, data) => {
  hideKioskInterfaceControls();
  const {
    meal_name = '',
    meal_type_name = '',
    meal_type_emoji = '',
    meal_date = '',
    meal_period = '',
    dining_hall = '',
    reservation_id = null,
    auto_delivery_mode = false,
    target_selector = '#checkin-flow-modal'
  } = data || {};

  const overlayId = 'checkin-confirm-modal';
  closeKnownKioskModals(hook, [overlayId]);

  const autoDeliveryBadge = auto_delivery_mode ? `
    <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 text-emerald-700 text-xs font-semibold uppercase tracking-[0.32em]">
      <span>Modo auto-entrega ativo</span>
    </div>
  ` : '';

  const staticOverlay = document.getElementById('checkin-confirm-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#checkin-confirm-content');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');
    const mealNameEl = staticOverlay.querySelector('[data-role="meal-name"]');
    const autoDeliveryBadgeEl = staticOverlay.querySelector('[data-role="auto-delivery-badge"]');
    const mealEmojiEl = staticOverlay.querySelector('[data-role="meal-emoji"]');
    const mealTypeNameEl = staticOverlay.querySelector('[data-role="meal-type-name"]');
    const mealDateEl = staticOverlay.querySelector('[data-role="meal-date"]');
    const mealPeriodEl = staticOverlay.querySelector('[data-role="meal-period"]');
    const diningHallEl = staticOverlay.querySelector('[data-role="dining-hall"]');
    const cancelBtn = staticOverlay.querySelector('[data-role="cancel"]');
    const confirmBtn = staticOverlay.querySelector('[data-role="confirm"]');
    const confirmLabel = staticOverlay.querySelector('[data-role="confirm-label"]');

    if (
      modal &&
      scrim &&
      mealNameEl &&
      autoDeliveryBadgeEl &&
      mealEmojiEl &&
      mealTypeNameEl &&
      mealDateEl &&
      mealPeriodEl &&
      diningHallEl &&
      cancelBtn &&
      confirmBtn &&
      confirmLabel
    ) {
      const defaultConfirmMarkup = `
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span class="tracking-wide uppercase">Validar Check-in</span>
        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"></path>
        </svg>
      `;

      const close = () => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => {
          staticOverlay.classList.add('hidden');
          showKioskInterfaceControls();
        }, 300);
      };

      mealNameEl.textContent = meal_name || '—';
      autoDeliveryBadgeEl.classList.toggle('hidden', !auto_delivery_mode);
      setOptionalTextIcon(mealEmojiEl, meal_type_emoji);
      mealTypeNameEl.textContent = meal_type_name || 'Não definido';
      mealDateEl.textContent = meal_date;
      mealPeriodEl.textContent = meal_period;
      diningHallEl.textContent = dining_hall;
      confirmBtn.disabled = false;
      confirmBtn.dataset.reservationId = reservation_id ?? '';
      confirmLabel.innerHTML = defaultConfirmMarkup;

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      cancelBtn.onclick = () => {
        close();
        sendKioskTargetedEvent(hook, target_selector, 'cancel_checkin_confirm', {});
      };

      confirmBtn.onclick = () => {
        confirmBtn.disabled = true;
        confirmLabel.innerHTML = `
          <span class="relative flex items-center justify-center gap-3">
            <svg class="w-6 h-6 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle class="opacity-20" cx="12" cy="12" r="10" stroke-width="2"/>
              <path class="opacity-80" stroke-linecap="round" stroke-width="2" d="M4 12a8 8 0 018-8"/>
            </svg>
            <span class="tracking-wide uppercase">A validar…</span>
          </span>
        `;

        sendKioskTargetedEvent(hook, target_selector, 'confirm_checkin', { reservation_id });
      };

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) {
          close();
          sendKioskTargetedEvent(hook, target_selector, 'cancel_checkin_confirm', {});
        }
      };

      return;
    }
  }

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="bg-gradient-to-br from-white/95 to-slate-50/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border-2 border-white/50 max-w-4xl w-full transform transition-all duration-500 scale-95 opacity-0" id="checkin-confirm-content">
        <div class="absolute top-0 right-0 h-48 w-48 bg-slate-300/30 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 h-40 w-40 bg-emerald-300/20 rounded-full blur-2xl"></div>

        <div class="relative p-12 md:p-16">
          <div class="text-center mb-10">
            <p class="text-sm uppercase tracking-[0.30em] text-slate-600 font-bold mb-4">Validar Refeição</p>
            <h2 class="text-4xl md:text-5xl font-black text-slate-900 leading-tight mb-2">${meal_name || '—'}</h2>
            ${autoDeliveryBadge}
          </div>

          <div class="grid sm:grid-cols-[auto,1fr] gap-8 items-start mb-12">
            <div class="flex justify-center">
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-600 rounded-3xl blur-xl opacity-40"></div>
                <div class="relative w-28 h-28 md:w-32 md:h-32 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-600 flex items-center justify-center shadow-2xl">
                  <svg class="h-16 w-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
                    <circle cx="12" cy="12" r="9" stroke-width="2"></circle>
                  </svg>
                </div>
              </div>
            </div>

            <div class="space-y-6">
              <div class="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-slate-100 text-sm font-semibold text-slate-700 uppercase tracking-[0.24em]">
                <span>Tipo</span>
                <span class="text-slate-500">•</span>
                <span>${meal_type_name || 'Não definido'}</span>
              </div>

              <div class="grid sm:grid-cols-3 gap-4">
                <div class="flex flex-col gap-2 rounded-2xl border-2 border-slate-200 bg-slate-50/80 backdrop-blur px-6 py-4">
                  <span class="text-xs uppercase tracking-[0.32em] text-slate-500 font-bold">Data</span>
                  <span class="text-lg font-bold text-slate-800">${meal_date}</span>
                </div>
                <div class="flex flex-col gap-2 rounded-2xl border-2 border-slate-200 bg-slate-50/80 backdrop-blur px-6 py-4">
                  <span class="text-xs uppercase tracking-[0.32em] text-slate-500 font-bold">Período</span>
                  <span class="text-lg font-bold text-slate-800">${meal_period}</span>
                </div>
                <div class="flex flex-col gap-2 rounded-2xl border-2 border-slate-200 bg-slate-50/80 backdrop-blur px-6 py-4">
                  <span class="text-xs uppercase tracking-[0.32em] text-slate-500 font-bold">Refeitório</span>
                  <span class="text-lg font-bold text-slate-800">${dining_hall}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-10 text-center">
            <p class="text-base text-slate-500 max-w-2xl mx-auto">
              Confirme visualmente que o prato corresponde à reserva antes de concluir a validação.
            </p>
          </div>

          <div class="flex flex-col sm:flex-row gap-5">
            <button
              id="checkin-cancel-btn"
              class="sm:w-auto px-8 py-5 rounded-2xl border-2 border-slate-300 bg-white/80 backdrop-blur text-slate-600 font-semibold text-lg transition-all duration-300 hover:bg-slate-50 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5 flex items-center justify-center gap-3">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              <span>Cancelar</span>
            </button>

            <button
              id="checkin-confirm-btn"
              class="flex-1 px-12 py-7 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-600 text-white font-bold text-2xl shadow-2xl transition-all duration-500 hover:shadow-[0_40px_120px_-50px_rgba(15,23,42,0.75)] hover:-translate-y-1 transform animate-pulse-subtle relative overflow-hidden group"
              data-reservation-id="${reservation_id}">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span class="relative flex items-center justify-center gap-4">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span class="tracking-wide uppercase">Validar Check-in</span>
                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('checkin-confirm-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = () => {
    if (modal) {
      modal.style.transform = 'scale(0.95)';
      modal.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        showKioskInterfaceControls();
      }, 300);
    }
  };

  document.getElementById('checkin-cancel-btn').addEventListener('click', () => {
    close();
    sendKioskTargetedEvent(hook, target_selector, 'cancel_checkin_confirm', {});
  });

  document.getElementById('checkin-confirm-btn').addEventListener('click', () => {
    const btn = document.getElementById('checkin-confirm-btn');
    btn.disabled = true;
    btn.innerHTML = `
      <span class="relative flex items-center justify-center gap-3">
        <svg class="w-6 h-6 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle class="opacity-20" cx="12" cy="12" r="10" stroke-width="2"/>
          <path class="opacity-80" stroke-linecap="round" stroke-width="2" d="M4 12a8 8 0 018-8"/>
        </svg>
        <span class="tracking-wide uppercase">A validar…</span>
      </span>
    `;

    sendKioskTargetedEvent(hook, target_selector, 'confirm_checkin', { reservation_id });
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      close();
      sendKioskTargetedEvent(hook, target_selector, 'cancel_checkin_confirm', {});
    }
  });
};

export const showCancellationSuccessModal = (_hook, data) => {
  const {
    consumer_name = '',
    meal_date = '',
    meal_period = '',
    dining_hall = ''
  } = data || {};

  const overlayId = 'cancellation-success-modal';
  const detailItems = [
    { label: 'Consumidor', value: consumer_name },
    { label: 'Refeitório', value: dining_hall },
    { label: 'Data', value: meal_date },
    { label: 'Período', value: meal_period }
  ].filter(({ value }) => !!value);

  const staticOverlay = document.getElementById('cancellation-success-modal');
  if (staticOverlay && staticOverlay.dataset.staticModalHost === 'true') {
    const modal = staticOverlay.querySelector('#cancellation-success-content');
    const scrim = staticOverlay.querySelector('[data-role="scrim"]');
    const detailsEl = staticOverlay.querySelector('[data-role="details"]');
    const detailsGrid = staticOverlay.querySelector('[data-role="details-grid"]');
    const closeBtn = staticOverlay.querySelector('[data-role="close"]');

    if (modal && scrim && detailsEl && detailsGrid && closeBtn) {
      const close = () => {
        modal.style.transform = 'scale(0.95)';
        modal.style.opacity = '0';
        setTimeout(() => {
          staticOverlay.classList.add('hidden');
        }, 300);
      };

      detailsEl.classList.toggle('hidden', detailItems.length === 0);
      populateDetailCards(detailsGrid, detailItems, {
        cardClass: 'flex flex-col gap-2 rounded-2xl border-2 border-emerald-200 bg-emerald-50/80 backdrop-blur px-6 py-4',
        labelClass: 'text-xs uppercase tracking-[0.32em] text-emerald-600 font-bold',
        valueClass: 'text-lg font-bold text-slate-800',
        labelTag: 'span',
        valueTag: 'span'
      });

      staticOverlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.style.transform = 'scale(1)';
        modal.style.opacity = '1';
      });

      staticOverlay.onclick = (event) => {
        if (event.target === staticOverlay || event.target === scrim) close();
      };
      closeBtn.onclick = () => close();
      setTimeout(close, 4000);

      return;
    }
  }

  const existing = document.getElementById(overlayId);
  if (existing) existing.remove();

  const html = `
    <div id="${overlayId}" class="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-[9999] p-4">
      <div class="bg-gradient-to-br from-white/95 to-slate-50/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border-2 border-white/50 max-w-2xl w-full transform transition-all duration-500 scale-95 opacity-0" id="cancellation-success-content">
        <div class="absolute top-0 right-0 h-48 w-48 bg-emerald-300/30 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 h-40 w-40 bg-green-300/20 rounded-full blur-2xl"></div>

        <div class="relative p-12 md:p-16">
          <div class="flex justify-center mb-8">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full blur-2xl opacity-50"></div>
              <div class="relative w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-2xl">
                <svg class="w-14 h-14 md:w-16 md:h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
          </div>

          <div class="text-center mb-10">
            <p class="text-sm uppercase tracking-[0.30em] text-emerald-600 font-bold mb-3">Operação Concluída</p>
            <h2 class="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-4">Reserva Cancelada com Sucesso</h2>
            <p class="text-base text-slate-600 max-w-lg mx-auto">A reserva foi cancelada e o sistema foi atualizado.</p>
          </div>

          ${detailItems.length > 0 ? `
          <div class="mb-10">
            <div class="grid sm:grid-cols-2 gap-4">
              ${detailItems.map(({ label, value }) => `
              <div class="flex flex-col gap-2 rounded-2xl border-2 border-emerald-200 bg-emerald-50/80 backdrop-blur px-6 py-4">
                <span class="text-xs uppercase tracking-[0.32em] text-emerald-600 font-bold">${label}</span>
                <span class="text-lg font-bold text-slate-800">${value}</span>
              </div>`).join('')}
            </div>
          </div>` : ''}

          <div class="flex justify-center">
            <button
              id="cancellation-close-btn"
              class="px-12 py-5 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold text-xl shadow-2xl transition-all duration-300 hover:shadow-emerald-500/50 hover:-translate-y-1 transform relative overflow-hidden group">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span class="relative flex items-center justify-center gap-3">
                <span class="tracking-wide uppercase">Fechar</span>
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const overlay = document.getElementById(overlayId);
  const modal = document.getElementById('cancellation-success-content');

  requestAnimationFrame(() => {
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  });

  const close = () => {
    modal.style.transform = 'scale(0.95)';
    modal.style.opacity = '0';
    setTimeout(() => {
      overlay.remove();
    }, 300);
  };

  document.getElementById('cancellation-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  setTimeout(close, 4000);
};

export const KioskBrowserModals = {
  kioskModalOverlayIds,
  removeKioskModals,
  closeKnownKioskModals,
  closeCheckinConfirmationModal,
  closeKioskSuccessModals,
  closeCheckinFlowBrowserModals,
  hideKioskInterfaceControls,
  showKioskInterfaceControls,
  syncKioskInterfaceControls,
  showModernAlert,
  showEmojiAlert,
  showOperationAlert,
  showOptionAction,
  showConfirmAlert,
  showWalkInConfirmModal,
  showWalkInConflictModal,
  showWalkInSuccessModal,
  showCheckinSuccessModal,
  showAlreadyCheckedInModal,
  showAlreadyServedModal,
  showCheckinConfirmModal,
  showCancellationSuccessModal
};
